import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { fetchData } from '../../Utils/Server';
import Endpoints from '../../Constants/Endpoints';
import PageLayout from '../Layout/PageLayout';
import { Card, StatusBadge, Spinner, tokens } from '../UI';

const { colors, spacing, radii, shadows, typography } = tokens;

/* ---------- helpers ---------- */

const pick = (log, snake, pascal) => log[snake] ?? log[pascal] ?? '';

const parseObj = (s) => {
  if (!s) return {};
  try {
    const v = JSON.parse(s);
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
  } catch {
    return null;
  }
};

const fmtVal = (v) =>
  v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);

const relTime = (d) => {
  const diff = (Date.now() - d.getTime()) / 1000;
  if (isNaN(diff)) return '';
  const abs = Math.abs(diff);
  const units = [
    [60, 'second'],
    [3600, 'minute'],
    [86400, 'hour'],
    [604800, 'day'],
    [2629800, 'week'],
    [31557600, 'month'],
  ];
  let label = 'just now';
  for (let i = 0; i < units.length; i++) {
    const [limit, name] = units[i];
    if (abs < limit) {
      const div = i === 0 ? 1 : units[i - 1][0];
      const n = Math.round(abs / div);
      if (n <= 1 && i === 0) { label = 'just now'; break; }
      label = `${n} ${name}${n === 1 ? '' : 's'} ${diff >= 0 ? 'ago' : 'from now'}`;
      break;
    }
    if (i === units.length - 1) {
      const n = Math.round(abs / 31557600);
      label = `${n} year${n === 1 ? '' : 's'} ${diff >= 0 ? 'ago' : 'from now'}`;
    }
  }
  return label;
};

/* action.verb → semantic tone for the accent */
const actionTone = (action) => {
  if (/delete|reject|cancel|suspend|debit|penalty|blacklist/.test(action)) return 'danger';
  if (/approve|reward|topup|credit|create|assign|disburse/.test(action)) return 'success';
  if (/refund|prepay|status/.test(action)) return 'warn';
  return 'brand';
};

/* ---------- styled ---------- */

const up = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; }`;

const Wrap = styled.div`
  max-width: 1040px;
  animation: ${up} 0.4s cubic-bezier(0.2, 0.75, 0.2, 1) both;
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${radii.lg};
  background: linear-gradient(135deg, ${colors.inkDeep}, ${colors.maroon} 130%);
  color: ${colors.onDark};
  padding: ${spacing[6]} ${spacing[6]};
  box-shadow: ${shadows.md};
  &::after {
    content: '';
    position: absolute;
    inset: 0 0 auto auto;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle at 70% 20%, rgba(233, 162, 59, 0.28), transparent 60%);
    pointer-events: none;
  }
`;

const Eyebrow = styled.div`
  font-family: ${typography.fonts.mono};
  font-size: ${typography.size.xs};
  letter-spacing: ${typography.tracking.wider};
  text-transform: uppercase;
  color: ${colors.onDarkMute || 'rgba(255,255,255,0.7)'};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};
`;

const Action = styled.h1`
  margin: 0;
  font-family: ${typography.fonts.mono};
  font-size: ${typography.size['3xl']};
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.tight};
  color: #fff;
  word-break: break-word;
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[3]} ${spacing[5]};
  margin-top: ${spacing[4]};
  font-family: ${typography.fonts.body};
  font-size: ${typography.size.sm};
  color: rgba(255, 255, 255, 0.82);
  span b { color: #fff; font-weight: ${typography.weight.semibold}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: ${spacing[1]};
  margin: ${spacing[5]} 0;
`;

const Field = styled.div`
  padding: ${spacing[4]};
  border-radius: ${radii.md};
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  .k {
    font-size: ${typography.size.xs};
    letter-spacing: ${typography.tracking.wide};
    text-transform: uppercase;
    color: ${colors.muted};
    font-weight: ${typography.weight.semibold};
    margin-bottom: ${spacing[2]};
  }
  .v {
    font-size: ${typography.size.md};
    color: ${colors.heading};
    font-weight: ${typography.weight.semibold};
    word-break: break-word;
  }
  .v.mono { font-family: ${typography.fonts.mono}; font-size: ${typography.size.base}; font-weight: ${typography.weight.medium}; }
  .v.faint { color: ${colors.muted}; font-weight: ${typography.weight.regular}; font-size: ${typography.size.base}; }
`;

const Reason = styled.div`
  position: relative;
  padding: ${spacing[4]} ${spacing[5]};
  padding-left: ${spacing[6]};
  border-radius: ${radii.md};
  background: ${colors.status?.brand?.bg || colors.rose};
  border: 1px solid ${colors.borderStrong};
  margin: ${spacing[5]} 0;
  &::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    background: ${colors.primary};
    border-radius: ${radii.md} 0 0 ${radii.md};
  }
  .label {
    font-size: ${typography.size.xs};
    letter-spacing: ${typography.tracking.wide};
    text-transform: uppercase;
    color: ${colors.primary};
    font-weight: ${typography.weight.bold};
    margin-bottom: ${spacing[1]};
  }
  .text { color: ${colors.heading}; font-size: ${typography.size.md}; line-height: ${typography.leading.normal}; }
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin: ${spacing[6]} 0 ${spacing[3]};
  font-weight: ${typography.weight.bold};
  color: ${colors.heading};
  font-size: ${typography.size.lg};
  .rule { flex: 1; height: 1px; background: ${colors.divider}; }
`;

const DiffTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-family: ${typography.fonts.mono};
  font-size: ${typography.size.base};
  th {
    text-align: left;
    font-family: ${typography.fonts.body};
    font-size: ${typography.size.xs};
    letter-spacing: ${typography.tracking.wide};
    text-transform: uppercase;
    color: ${colors.muted};
    font-weight: ${typography.weight.semibold};
    padding: 0 ${spacing[4]} ${spacing[2]};
  }
  td {
    padding: ${spacing[3]} ${spacing[4]};
    border-top: 1px solid ${colors.divider};
    vertical-align: top;
    word-break: break-word;
  }
  td.key { color: ${colors.heading}; font-weight: ${typography.weight.semibold}; white-space: nowrap; }
  tr.changed td { background: #fffdf7; }
  .before { color: ${colors.status?.danger?.fg || '#a3121c'}; }
  .after  { color: ${colors.status?.success?.fg || '#1b7a43'}; font-weight: ${typography.weight.semibold}; }
  .same   { color: ${colors.muted}; }
  .arrow  { color: ${colors.muted}; padding: 0 ${spacing[1]}; }
`;

const Raw = styled.pre`
  margin: 0;
  padding: ${spacing[4]};
  border-radius: ${radii.md};
  background: ${colors.surfaceSunken};
  border: 1px solid ${colors.border};
  font-family: ${typography.fonts.mono};
  font-size: ${typography.size.sm};
  line-height: ${typography.leading.normal};
  color: ${colors.heading};
  overflow-x: auto;
  white-space: pre-wrap;
`;

const RawGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[4]};
  @media (max-width: 720px) { grid-template-columns: 1fr; }
`;

/* ---------- component ---------- */

export default function AuditLogDetails() {
  const { id } = useParams();
  const [log, setLog] = useState(null);

  useEffect(() => {
    fetchData(Endpoints.GET_AUDIT(id))
      .then((res) => res.json())
      .then(setLog);
  }, [id]);

  if (!log) return <Spinner center label="Loading audit entry…" />;

  const action = pick(log, 'action', 'Action');
  const actorId = pick(log, 'actor_id', 'ActorID');
  const actorRole = pick(log, 'actor_role', 'ActorRole');
  const resourceType = pick(log, 'resource_type', 'ResourceType');
  const resourceId = pick(log, 'resource_id', 'ResourceID');
  const reason = pick(log, 'reason', 'Reason');
  const ip = pick(log, 'ip', 'IP');
  const userAgent = pick(log, 'user_agent', 'UserAgent');
  const beforeRaw = pick(log, 'before_json', 'BeforeJSON');
  const afterRaw = pick(log, 'after_json', 'AfterJSON');
  const created = new Date(pick(log, 'created_at', 'CreatedAt'));

  const before = parseObj(beforeRaw);
  const after = parseObj(afterRaw);
  const canDiff = before && after && (Object.keys(before).length || Object.keys(after).length);

  let diffRows = [];
  if (canDiff) {
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
    diffRows = keys.map((k) => {
      const b = before[k];
      const a = after[k];
      const changed = JSON.stringify(b) !== JSON.stringify(a);
      return { k, b, a, changed };
    });
  }

  return (
    <PageLayout title="Audit entry">
      <Wrap>
        <Hero>
          <Eyebrow>
            Audit entry
            <span style={{ opacity: 0.6 }}>·</span>
            {String(pick(log, 'id', 'ID')).slice(0, 12)}
          </Eyebrow>
          <Action>{action || 'unknown action'}</Action>
          <HeroMeta>
            <span><StatusBadge tone={actionTone(action)} size="sm">{resourceType || 'resource'}</StatusBadge></span>
            <span>Resource&nbsp;<b>{resourceId || '—'}</b></span>
            <span title={created.toString()}>{isNaN(created) ? '—' : `${created.toLocaleString()} · ${relTime(created)}`}</span>
          </HeroMeta>
        </Hero>

        <Grid>
          <Field>
            <div className="k">Actor</div>
            <div className="v">{actorId || '—'}</div>
            <div style={{ marginTop: 6 }}>
              <StatusBadge tone="neutral" size="sm">{actorRole || 'unknown role'}</StatusBadge>
            </div>
          </Field>
          <Field>
            <div className="k">Resource</div>
            <div className="v">{resourceType || '—'}</div>
            <div className="v mono" style={{ marginTop: 4 }}>{resourceId || '—'}</div>
          </Field>
          <Field>
            <div className="k">Origin IP</div>
            <div className="v mono">{ip || '—'}</div>
          </Field>
          <Field>
            <div className="k">User agent</div>
            <div className="v faint">{userAgent || '—'}</div>
          </Field>
        </Grid>

        <Reason>
          <div className="label">Reason on record</div>
          <div className="text">{reason || <span style={{ color: colors.muted }}>No reason recorded.</span>}</div>
        </Reason>

        <SectionTitle>
          Change diff
          <span className="rule" />
        </SectionTitle>

        {canDiff ? (
          <Card padded={false}>
            <div style={{ padding: `${spacing[4]} 0` }}>
              <DiffTable>
                <thead>
                  <tr>
                    <th style={{ width: '26%' }}>Field</th>
                    <th style={{ width: '37%' }}>Before</th>
                    <th style={{ width: '37%' }}>After</th>
                  </tr>
                </thead>
                <tbody>
                  {diffRows.map((r) => (
                    <tr key={r.k} className={r.changed ? 'changed' : ''}>
                      <td className="key">{r.k}</td>
                      <td className={r.changed ? 'before' : 'same'}>{fmtVal(r.b)}</td>
                      <td className={r.changed ? 'after' : 'same'}>{fmtVal(r.a)}</td>
                    </tr>
                  ))}
                </tbody>
              </DiffTable>
            </div>
          </Card>
        ) : (
          <RawGrid>
            <div>
              <div style={{ fontSize: 12, color: colors.muted, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Before</div>
              <Raw>{beforeRaw ? (before ? JSON.stringify(before, null, 2) : beforeRaw) : '—'}</Raw>
            </div>
            <div>
              <div style={{ fontSize: 12, color: colors.muted, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>After</div>
              <Raw>{afterRaw ? (after ? JSON.stringify(after, null, 2) : afterRaw) : '—'}</Raw>
            </div>
          </RawGrid>
        )}

        <div style={{ marginTop: spacing[6] }}>
          <Link to="/audit" style={{ color: colors.primary, fontWeight: 600 }}>← Back to audit log</Link>
        </div>
      </Wrap>
    </PageLayout>
  );
}
